import React, { useState, useEffect, useMemo } from 'react'

const NAVY = '#003087'
const RED = '#CE1126'
const GOLD = '#F5A623'
const LIGHT_RED = '#fde8e8'
const LIGHT_GREEN = '#e6f4ea'
const LIGHT_GOLD = '#fef7e0'

function HockeyIntelligence() {
  const [data, setData] = useState(null)
  const [view, setView] = useState('coaching')
  const [groupFilter, setGroupFilter] = useState('All')
  const [sortCol, setSortCol] = useState('gaa')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    fetch('/intelligence_brief.json')
      .then(r => r.json())
      .then(setData)
      .catch(() => console.error('Failed to load intelligence_brief.json'))
  }, [])

  if (!data) return <div style={{padding:40,textAlign:'center',color:'#666'}}>Loading intelligence data...</div>

  const rye = data.rye_baseline
  const gap = data.gap_analysis
  const targets = data.championship_targets
  const teams = data.competition_field || []

  return (
    <div style={{maxWidth:1200,margin:'0 auto',padding:'24px 16px'}}>
      <Header view={view} setView={setView} />
      {view === 'coaching'
        ? <CoachingView rye={rye} gap={gap} targets={targets} teams={teams}
            groupFilter={groupFilter} setGroupFilter={setGroupFilter}
            sortCol={sortCol} setSortCol={setSortCol} sortAsc={sortAsc} setSortAsc={setSortAsc}
            top3={data.top3_benchmark} periodData={data.period_data} />
        : <PlayerView rye={rye} gap={gap} targets={targets} />
      }
    </div>
  )
}

function Header({ view, setView }) {
  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:32,flexWrap:'wrap',gap:16}}>
      <div>
        <h1 style={{fontFamily:'Georgia,serif',fontSize:'1.6rem',color:NAVY,margin:0}}>
          Rye Rangers 14U AA <span style={{color:RED}}>|</span> Championship Intelligence Room
        </h1>
        <p style={{fontSize:'0.85rem',color:'#666',marginTop:4}}>2024 Season Analysis</p>
      </div>
      <div style={{display:'flex',gap:8}}>
        <button onClick={() => setView('coaching')}
          style={{padding:'8px 20px',border:`2px solid ${NAVY}`,borderRadius:4,cursor:'pointer',fontWeight:600,fontSize:'0.85rem',
            background: view === 'coaching' ? NAVY : '#fff', color: view === 'coaching' ? '#fff' : NAVY}}>
          Coaching View
        </button>
        <button onClick={() => setView('player')}
          style={{padding:'8px 20px',border:`2px solid ${RED}`,borderRadius:4,cursor:'pointer',fontWeight:600,fontSize:'0.85rem',
            background: view === 'player' ? RED : '#fff', color: view === 'player' ? '#fff' : RED}}>
          Player View
        </button>
      </div>
    </div>
  )
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div style={{background:'#fff',border:'1px solid #e0e0e0',borderRadius:8,padding:'20px 16px',flex:1,minWidth:180,borderTop:`3px solid ${color || NAVY}`}}>
      <div style={{fontSize:'0.75rem',color:'#888',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8}}>{label}</div>
      <div style={{fontSize:'2rem',fontWeight:700,color:color || NAVY}}>{value}</div>
      {sub && <div style={{fontSize:'0.8rem',color:'#666',marginTop:4}}>{sub}</div>}
    </div>
  )
}

function GapBar({ label, ryeVal, targetVal, eliteVal, min, max, unit }) {
  const range = max - min || 1
  const ryePos = ((ryeVal - min) / range) * 100
  const targetPos = ((targetVal - min) / range) * 100
  const elitePos = ((eliteVal - min) / range) * 100
  return (
    <div style={{marginBottom:24}}>
      <div style={{fontSize:'0.85rem',fontWeight:600,marginBottom:8}}>{label}</div>
      <div style={{position:'relative',height:32,background:'#eee',borderRadius:4}}>
        <div style={{position:'absolute',left:`${Math.min(100,Math.max(0,elitePos))}%`,top:0,bottom:0,width:2,background:'#2ecc71',zIndex:2}} title={`Elite: ${eliteVal}${unit}`} />
        <div style={{position:'absolute',left:`${Math.min(100,Math.max(0,targetPos))}%`,top:0,bottom:0,width:2,background:GOLD,zIndex:2}} title={`Target: ${targetVal}${unit}`} />
        <div style={{position:'absolute',left:`${Math.min(100,Math.max(0,ryePos))}%`,top:0,bottom:0,width:4,background:RED,zIndex:3,borderRadius:2}} title={`Rye: ${ryeVal}${unit}`} />
      </div>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:'0.7rem',color:'#999',marginTop:4}}>
        <span>{min}{unit}</span>
        <div style={{display:'flex',gap:16}}>
          <span style={{color:'#2ecc71'}}>Elite: {eliteVal}{unit}</span>
          <span style={{color:GOLD}}>Target: {targetVal}{unit}</span>
          <span style={{color:RED}}>Rye: {ryeVal}{unit}</span>
        </div>
        <span>{max}{unit}</span>
      </div>
    </div>
  )
}

function CoachingView({ rye, gap, targets, teams, groupFilter, setGroupFilter, sortCol, setSortCol, sortAsc, setSortAsc, top3, periodData }) {
  const groups = ['All', ...new Set(teams.map(t => t.group))]

  const filteredTeams = useMemo(() => {
    let list = groupFilter === 'All' ? teams : teams.filter(t => t.group === groupFilter)
    list = [...list].sort((a, b) => {
      const av = a[sortCol] ?? 999
      const bv = b[sortCol] ?? 999
      return sortAsc ? av - bv : bv - av
    })
    return list
  }, [teams, groupFilter, sortCol, sortAsc])

  const handleSort = (col) => {
    if (sortCol === col) setSortAsc(!sortAsc)
    else { setSortCol(col); setSortAsc(col === 'gaa') }
  }

  const gaaVals = teams.map(t => t.gaa).filter(Boolean)
  const gfpgVals = teams.map(t => t.gfpg).filter(Boolean)
  const winVals = teams.map(t => t.win_pct).filter(Boolean)

  return (
    <>
      {/* Metric Cards */}
      <div style={{display:'flex',gap:16,marginBottom:32,flexWrap:'wrap'}}>
        <MetricCard label="Rye GAA" value={rye.gaa} sub="Goals against per game" color={RED} />
        <MetricCard label="Target GAA" value={targets.state_contender_gaa} sub="State contender threshold" color={GOLD} />
        <MetricCard label="Elite GAA" value={targets.elite_gaa} sub={targets.elite_gaa_team} color="#2ecc71" />
        <MetricCard label="Rye Win %" value={`${rye.win_pct}%`} sub={`${rye.w}-${rye.l}-${rye.t}`} color={RED} />
        <MetricCard label="Elite Win %" value={`${Math.max(...winVals)}%`} sub="Best in field" color="#2ecc71" />
      </div>

      {/* Gap Bars */}
      <div style={{background:'#fff',border:'1px solid #e0e0e0',borderRadius:8,padding:24,marginBottom:32}}>
        <h3 style={{fontFamily:'Georgia,serif',fontSize:'1.1rem',color:NAVY,marginBottom:20}}>Gap Analysis</h3>
        <GapBar label="Goals Against Average (lower is better)" ryeVal={rye.gaa} targetVal={targets.state_contender_gaa} eliteVal={targets.elite_gaa} min={Math.min(...gaaVals) - 0.5} max={Math.max(...gaaVals) + 0.5} unit="" />
        <GapBar label="Goals For Per Game (higher is better)" ryeVal={rye.gfpg} targetVal={targets.state_contender_gfpg} eliteVal={targets.elite_gfpg} min={Math.min(...gfpgVals) - 0.5} max={Math.max(...gfpgVals) + 0.5} unit="" />
        <GapBar label="Win Percentage (higher is better)" ryeVal={rye.win_pct} targetVal={targets.state_contender_win_pct} eliteVal={Math.max(...winVals)} min={0} max={100} unit="%" />
      </div>

      {/* Callout Boxes */}
      <div style={{display:'flex',gap:16,marginBottom:32,flexWrap:'wrap'}}>
        <div style={{flex:1,minWidth:300,background:'#fff',border:`2px solid ${RED}`,borderRadius:8,padding:24}}>
          <h3 style={{color:RED,fontFamily:'Georgia,serif',fontSize:'1rem',marginBottom:12}}>The Gap</h3>
          <p style={{fontSize:'0.9rem',lineHeight:1.6,color:'#333'}}>{gap.gaa.games_impact}</p>
          <p style={{fontSize:'0.85rem',color:'#666',marginTop:12}}>{gap.gaa.narrative}</p>
        </div>
        <div style={{flex:1,minWidth:300,background:'#fff',border:`2px solid #2ecc71`,borderRadius:8,padding:24}}>
          <h3 style={{color:'#2ecc71',fontFamily:'Georgia,serif',fontSize:'1rem',marginBottom:12}}>The Opportunity</h3>
          <p style={{fontSize:'0.9rem',lineHeight:1.6,color:'#333'}}>{gap.gfpg.narrative}</p>
          <p style={{fontSize:'0.85rem',color:'#666',marginTop:12}}>{gap.gfpg.coaching_lever}</p>
        </div>
      </div>

      {/* Competition Field */}
      <div style={{background:'#fff',border:'1px solid #e0e0e0',borderRadius:8,padding:24,marginBottom:32,overflowX:'auto'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
          <h3 style={{fontFamily:'Georgia,serif',fontSize:'1.1rem',color:NAVY}}>Competition Field</h3>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            {groups.map(g => (
              <button key={g} onClick={() => setGroupFilter(g)}
                style={{padding:'4px 12px',border:'1px solid #ccc',borderRadius:4,cursor:'pointer',fontSize:'0.75rem',
                  background: groupFilter === g ? NAVY : '#fff', color: groupFilter === g ? '#fff' : '#333'}}>
                {g}
              </button>
            ))}
          </div>
        </div>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
          <thead>
            <tr style={{borderBottom:`2px solid ${NAVY}`}}>
              {[['#',''],['Team','name'],['St','state'],['W-L-T',''],['GAA','gaa'],['GF/G','gfpg'],['Win%','win_pct'],['+/-/G','goal_diff_pg'],['Tier','']].map(([label,col]) => (
                <th key={label} onClick={() => col && handleSort(col)}
                  style={{padding:'8px 6px',textAlign:'left',cursor: col ? 'pointer' : 'default',color:NAVY,userSelect:'none',whiteSpace:'nowrap'}}>
                  {label} {col && sortCol === col ? (sortAsc ? '\u25B2' : '\u25BC') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredTeams.map((t, i) => {
              const isRye = t.name.toLowerCase().includes('rye')
              const gaaBg = t.gaa < 2.0 ? LIGHT_GREEN : t.gaa < 2.5 ? LIGHT_GOLD : t.gaa >= 3.0 ? LIGHT_RED : 'transparent'
              return (
                <tr key={t.team_id || i} style={{borderBottom:'1px solid #eee', background: isRye ? '#fde8e8' : 'transparent', fontWeight: isRye ? 600 : 400}}>
                  <td style={{padding:'6px'}}>{i + 1}</td>
                  <td style={{padding:'6px',color: isRye ? RED : '#333'}}>{t.name}</td>
                  <td style={{padding:'6px',color:'#888'}}>{t.state}</td>
                  <td style={{padding:'6px'}}>{t.w}-{t.l}-{t.t}</td>
                  <td style={{padding:'6px',background:gaaBg,fontWeight:600}}>{t.gaa}</td>
                  <td style={{padding:'6px'}}>{t.gfpg}</td>
                  <td style={{padding:'6px'}}>{t.win_pct}%</td>
                  <td style={{padding:'6px',color: t.goal_diff_pg > 0 ? '#2ecc71' : t.goal_diff_pg < 0 ? RED : '#888'}}>{t.goal_diff_pg > 0 ? '+' : ''}{t.goal_diff_pg}</td>
                  <td style={{padding:'6px'}}><span style={{fontSize:'0.7rem',padding:'2px 8px',borderRadius:4,
                    background: t.tier === 'elite' ? '#2ecc71' : t.tier === 'contender' ? GOLD : t.tier === 'competitive' ? '#ddd' : t.tier === 'developing' ? '#fdd' : '#f99',
                    color: t.tier === 'elite' ? '#fff' : t.tier === 'contender' ? '#fff' : '#333'}}>{t.tier}</span></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

function PlayerView({ rye, gap, targets }) {
  const extraGoals = Math.round(gap.gaa.gap_to_target * rye.gp)

  return (
    <div style={{maxWidth:700,margin:'0 auto'}}>
      <div style={{display:'flex',flexDirection:'column',gap:24,marginBottom:40}}>
        <div style={{background:'#fff',border:'1px solid #e0e0e0',borderRadius:8,padding:40,textAlign:'center'}}>
          <div style={{fontSize:'4rem',fontWeight:700,color:RED}}>{rye.gaa}</div>
          <div style={{fontSize:'1.1rem',color:'#333',marginTop:8}}>goals we let in every game last year</div>
        </div>
        <div style={{background:'#fff',border:'1px solid #e0e0e0',borderRadius:8,padding:40,textAlign:'center'}}>
          <div style={{fontSize:'4rem',fontWeight:700,color:'#2ecc71'}}>{rye.gfpg}</div>
          <div style={{fontSize:'1.1rem',color:'#333',marginTop:8}}>goals we scored every game. We can score. That is not the problem.</div>
        </div>
        <div style={{background:'#fff',border:'1px solid #e0e0e0',borderRadius:8,padding:40,textAlign:'center'}}>
          <div style={{fontSize:'4rem',fontWeight:700,color:NAVY}}>{extraGoals}</div>
          <div style={{fontSize:'1.1rem',color:'#333',marginTop:8,lineHeight:1.6}}>
            extra goals we gave up over the season compared to a championship team.
            <br />{extraGoals} decisions. {extraGoals} moments we are coaching differently.
          </div>
        </div>
      </div>
      <div style={{background:NAVY,borderRadius:8,padding:40,textAlign:'center'}}>
        <div style={{fontSize:'2.4rem',fontWeight:700,color:GOLD}}>{targets.state_contender_gaa} or better</div>
        <div style={{fontSize:'1rem',color:'rgba(255,255,255,0.85)',marginTop:12}}>
          That is the standard. Every drill this year closes that gap.
        </div>
      </div>
    </div>
  )
}

export default HockeyIntelligence
